/**
 * Address utility functions for cleaning and formatting DealerCenter import data
 * Handles edge cases where corrupted data might still exist
 */

export interface CleanAddress {
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
}

/**
 * Clean and parse potentially corrupted address field
 * Handles DealerCenter import artifacts and workflow data
 */
export function cleanAddressField(addressField: string | null | undefined): string | null {
  if (!addressField || typeof addressField !== 'string') {
    return null;
  }

  let cleaned = addressField.trim();

  // Remove common DealerCenter artifacts
  const artifacts = [
    /,Workflow.*/g,
    /,Credit App.*/g,
    /,OFAC.*/g,
    /,Last Contacted.*/g,
    /,Income.*/g,
    /,Day,\d+.*/g,
    /,"\$[\d,]+".*/g,
    /,[A-Z0-9]{17}.*/g, // VIN patterns
    /,Completed.*/g,
    /,Yes.*/g,
    /,No.*/g,
  ];

  for (const artifact of artifacts) {
    cleaned = cleaned.replace(artifact, '');
  }

  // Clean up quotes and extra spaces
  cleaned = cleaned.replace(/^["']+|["']+$/g, '').trim();

  // If what's left is too short or just punctuation, return null
  if (cleaned.length < 3 || /^[,\s]*$/.test(cleaned)) {
    return null;
  }

  return cleaned;
}

/**
 * Format address for display
 * Handles null values and provides clean formatting
 */
export function formatDisplayAddress(
  street?: string | null,
  city?: string | null,
  state?: string | null,
  zip?: string | null
): string {
  const parts = [];

  if (street) {
    const cleanStreet = cleanAddressField(street);
    if (cleanStreet) parts.push(cleanStreet);
  }

  if (city) {
    const cleanCity = cleanAddressField(city);
    if (cleanCity) parts.push(cleanCity);
  }

  if (state && zip) {
    const cleanState = cleanAddressField(state);
    const cleanZip = cleanAddressField(zip);
    if (cleanState && cleanZip) {
      parts.push(`${cleanState} ${cleanZip}`);
    } else if (cleanState) {
      parts.push(cleanState);
    } else if (cleanZip) {
      parts.push(cleanZip);
    }
  } else if (state) {
    const cleanState = cleanAddressField(state);
    if (cleanState) parts.push(cleanState);
  } else if (zip) {
    const cleanZip = cleanAddressField(zip);
    if (cleanZip) parts.push(cleanZip);
  }

  return parts.length > 0 ? parts.join(', ') : 'No address on file';
}

/**
 * Parse a potentially corrupted street address field into components
 * Useful for the edit modal when the field might contain multiple parts
 */
export function parseStreetAddress(streetField: string | null | undefined): CleanAddress {
  const result: CleanAddress = {
    street_address: null,
    city: null,
    state: null,
    zip_code: null,
  };

  if (!streetField || typeof streetField !== 'string') {
    return result;
  }

  // First clean the field
  const cleaned = cleanAddressField(streetField);
  if (!cleaned) {
    return result;
  }

  // If it looks like a simple address, return as is
  if (!cleaned.includes(',')) {
    result.street_address = cleaned;
    return result;
  }

  // Split by comma and try to parse components
  const parts = cleaned
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 1) {
    result.street_address = parts[0];
  } else if (parts.length === 2) {
    // Could be "Street, City" or "Street, State ZIP"
    result.street_address = parts[0];

    // Check if second part looks like "State ZIP"
    const stateZipMatch = parts[1].match(/^([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/);
    if (stateZipMatch) {
      result.state = stateZipMatch[1];
      result.zip_code = stateZipMatch[2];
    } else {
      result.city = parts[1];
    }
  } else if (parts.length >= 3) {
    // "Street, City, State" or "Street, City, State ZIP"
    result.street_address = parts[0];
    result.city = parts[1];

    // Parse state and ZIP from third part
    const stateZipMatch = parts[2].match(/^([A-Z]{2})(?:\s+(\d{5}(?:-\d{4})?))?$/);
    if (stateZipMatch) {
      result.state = stateZipMatch[1];
      if (stateZipMatch[2]) {
        result.zip_code = stateZipMatch[2];
      }
    }
  }

  return result;
}

/**
 * Validate if an address component looks corrupted
 * Used to detect fields that might need cleaning
 */
export function isCorruptedAddressComponent(value: string | null | undefined): boolean {
  if (!value || typeof value !== 'string') {
    return false;
  }

  const corruptionIndicators = [
    'Workflow',
    'Credit App',
    'OFAC',
    'Last Contacted',
    'Income',
    'Completed',
    'Day,',
    '$',
    /[A-Z0-9]{17}/, // VIN pattern
    /,{2,}/, // Multiple consecutive commas
  ];

  for (const indicator of corruptionIndicators) {
    if (typeof indicator === 'string') {
      if (value.includes(indicator)) return true;
    } else {
      if (indicator.test(value)) return true;
    }
  }

  // Check for unusually long strings (likely contain extra data)
  if (value.length > 100) return true;

  return false;
}

const addressUtils = {
  cleanAddressField,
  formatDisplayAddress,
  parseStreetAddress,
  isCorruptedAddressComponent,
};

export default addressUtils;
